import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { fadeRise } from './motion'

export interface FilterMenuOption<T extends string> {
  key: T
  label: string
}

interface Props<T extends string> {
  options: FilterMenuOption<T>[]
  value: T
  onChange: (next: T) => void
  /** What the filter is, for screen readers: "Period filter". */
  'aria-label': string
  /** Which edge the list lines up with. The right-most menu should open leftwards. */
  align?: 'start' | 'end'
}

/**
 * A filter that shows only its current value, as a chip with a chevron.
 *
 * For filters, not views: the choice narrows what is already on screen, and you
 * rarely need every option in sight. Standings used two rows of chips for its
 * period and gender, which pushed the board down; two of these fit beside the
 * title. Switching between views stays `ChipTabs`.
 *
 * The list opens inside the page rather than in a portal. HeroUI's dropdown
 * portals at a lower layer than our bottom sheet, so inside the Standings sheet
 * it would open behind it.
 */
export function FilterMenu<T extends string>({
  options,
  value,
  onChange,
  'aria-label': ariaLabel,
  align = 'start',
}: Props<T>) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([])
  const listId = useId()

  const current = options.find((o) => o.key === value) ?? options[0]

  // Close on a tap anywhere else.
  useEffect(() => {
    if (!open) return
    const onPointer = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', onPointer)
    return () => document.removeEventListener('pointerdown', onPointer)
  }, [open])

  // Land on the selected option, so arrows move from where you are.
  useEffect(() => {
    if (!open) return
    const index = Math.max(
      0,
      options.findIndex((o) => o.key === value),
    )
    optionRefs.current[index]?.focus()
  }, [open, options, value])

  const close = () => {
    setOpen(false)
    triggerRef.current?.focus()
  }

  const choose = (key: T) => {
    if (key !== value) onChange(key)
    close()
  }

  const onListKey = (e: KeyboardEvent, index: number) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      close()
      return
    }
    const step = e.key === 'ArrowDown' ? 1 : e.key === 'ArrowUp' ? -1 : 0
    if (!step) return
    e.preventDefault()
    optionRefs.current[(index + step + options.length) % options.length]?.focus()
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={`${ariaLabel}: ${current.label}`}
        onClick={() => setOpen((o) => !o)}
        className={`flex h-8 items-center gap-1 rounded-full border bg-content1 pl-3.5 pr-2.5 text-[13px] font-semibold text-foreground outline-none transition-colors focus-visible:ring-2 focus-visible:ring-[var(--gym-primary)] focus-visible:ring-offset-2 ${
          open ? 'border-foreground' : 'border-[var(--gym-line)] hover:border-foreground/30'
        }`}
      >
        <span className="whitespace-nowrap">{current.label}</span>
        <svg
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
          className={`h-4 w-4 text-foreground/50 transition-transform ${open ? 'rotate-180' : ''}`}
        >
          <path
            fillRule="evenodd"
            d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            id={listId}
            role="listbox"
            aria-label={ariaLabel}
            variants={fadeRise}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className={`absolute top-full z-10 mt-1.5 flex min-w-44 flex-col rounded-md border border-[var(--gym-line)] bg-content1 p-1 shadow-e3 ${
              align === 'end' ? 'right-0' : 'left-0'
            }`}
          >
            {options.map((option, i) => {
              const selected = option.key === value
              return (
                <button
                  key={option.key}
                  ref={(el) => {
                    optionRefs.current[i] = el
                  }}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => choose(option.key)}
                  onKeyDown={(e) => onListKey(e, i)}
                  className={`flex items-center justify-between gap-3 rounded-sm px-3 py-2.5 text-left text-sm outline-none focus-visible:bg-foreground/5 hover:bg-foreground/5 ${
                    selected ? 'font-semibold text-foreground' : 'text-foreground/75'
                  }`}
                >
                  <span className="whitespace-nowrap">{option.label}</span>
                  {selected && (
                    <svg
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                      className="h-4 w-4 text-[var(--gym-primary)]"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </button>
              )
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
